import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  password: string | null;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  authRequired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'tubemeup_auth_password';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [password, setPassword] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if auth is required
    fetch('/api/health')
      .then((res) => res.json())
      .then((data) => {
        setAuthRequired(data.authRequired || false);

        // If auth is required, try to load saved password
        if (data.authRequired) {
          const savedPassword = localStorage.getItem(AUTH_STORAGE_KEY);
          if (savedPassword) {
            setPassword(savedPassword);
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  const login = async (pwd: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: pwd }),
      });

      if (response.ok) {
        setPassword(pwd);
        localStorage.setItem(AUTH_STORAGE_KEY, pwd);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const logout = () => {
    setPassword(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const isAuthenticated = !authRequired || !!password;

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, password, login, logout, authRequired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
